import {
  ChartProps,
  HorizontalBarChart
} from '@fluentui/react-charts';
import { Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { JSX, ReactElement, useEffect, useState } from 'react';
import { LightGray } from '../../common/types/color-types';
import './RoundedHorizontalBarChart.scss';
import { FlexBox } from './flex-box';

interface BarSegment {
  name: string;
  color: string;
  value: number;
}

interface RoundedHorizontalBarChartProps {
  id: string;
  primaryData: BarSegment;
  secondaryData: BarSegment;
  renderDescription?: ReactElement;
}

const RoundedHorizontalBarChartImpl: React.FC<RoundedHorizontalBarChartProps> = (props) => {
  const {
    id,
    primaryData,
    secondaryData,
    renderDescription,
  } = props;

  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);
  const total = primaryData.value + secondaryData.value;

  const chartData: ChartProps[] = [
    {
      chartData: total === 0
        ?
        [
          {
            horizontalBarChartdata: { x: 1, total: 1 },
            color: LightGray,
          },
        ]
        :
        [
          {
            legend: primaryData.name,
            horizontalBarChartdata: { x: primaryData.value, total },
            color: primaryData.color,
          },
          {
            legend: secondaryData.name,
            horizontalBarChartdata: { x: secondaryData.value, total },
            color: secondaryData.color,
          },
        ],
    },
  ];

  const renderCallout = (): JSX.Element | undefined => {
    if (total === 0) {
      return <Text>No data available</Text>;
    }
    return undefined;
  };

  useEffect(() => {
    if (!chartNode) return;

    // Removing chart spacing style between segments.
    const adjustBars = (): boolean => {
      const chartRectElements = chartNode.querySelectorAll(`#${id} .fui-hbc__barWrapper`);
      const rectWidth = chartRectElements[0].getAttribute('width');
      if (!rectWidth) return false;
      chartRectElements[1].setAttribute('x', `${rectWidth}`);
      return true;
    };

    if (adjustBars()) return;

    const observer = new MutationObserver(() => {
      if (adjustBars()) observer.disconnect();
    });

    observer.observe(chartNode, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => observer.disconnect();
  }, [chartNode, id, primaryData.value, secondaryData.value]);

  return (
    <FlexBox
      id={id}
      direction='column'
      className='rounded-horizontal-bar-chart'
    >
      <div
        ref={setChartNode}
        className='rounded-horizontal-bar-chart__chart'
      >
        {
          < HorizontalBarChart
            data={chartData}
            barHeight={40}
            hideLabels
            chartDataMode='default'
            onRenderCalloutPerHorizontalBar={renderCallout}
          />
        }
      </div>
      {
        renderDescription ? renderDescription : (
          <FlexBox
            content='space-between'
            className='rounded-horizontal-bar-chart__legends'
          >
            <span>{`${primaryData.name}: ${primaryData.value}`}</span>
            <span>{`${secondaryData.name}: ${secondaryData.value}`}</span>
          </FlexBox>
        )
      }
    </FlexBox>
  );
};

export const RoundedHorizontalBarChart = observer(RoundedHorizontalBarChartImpl);
